import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ClipboardPaste, ExternalLink, Globe, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/components/ConfirmDialog';
import { wdel, wget, wpost } from '@/components/website/api';
import { PasteImportDialog } from '@/components/website/PasteImportDialog';
import { WebsiteEntryDialog, STATUS_META } from '@/components/website/WebsiteEntryDialog';
import { RenewalCell, renewalState } from '@/components/website/RenewalCell';
import { HostingInvoiceCell } from '@/components/website/HostingInvoiceCell';
import { HostingInvoiceDialog } from '@/components/website/HostingInvoiceDialog';

const FILTERS = [['all', 'All'], ['live', 'Live'], ['due', 'Hosting due soon'], ['unpaid', 'Unpaid'], ['missing_renewal', 'Missing renewal date'], ['offline', 'Offline'], ['transferred', 'Transferred'], ['portal', 'Built via portal']];
const matches = (e, filter) => {
  if (filter === 'all') return true;
  if (filter === 'portal') return e.source === 'portal';
  if (filter === 'missing_renewal') return renewalState(e) === 'missing';
  if (filter === 'due') return ['soon', 'overdue'].includes(renewalState(e));
  if (filter === 'unpaid') return !!e.unpaid_overdue;
  return e.status === filter;
};

function Row({ e, onEdit, onDelete, onRenewal, onRaise, onTakeDown, navigate }) {
  const st = STATUS_META[e.status] || STATUS_META.unknown;
  const portal = e.source === 'portal';
  const missing = renewalState(e) === 'missing';
  const tone = e.unpaid_overdue ? 'bg-rose-50 border-rose-300 border-l-4' : missing ? 'bg-amber-50 border-amber-300 border-l-4' : 'bg-white border-gray-200';
  return (
    <div className={`border rounded-xl p-3 sm:px-4 flex flex-wrap items-start gap-x-4 gap-y-2 ${tone}`} data-testid={`wl-row-${e.id}`} data-renewal-missing={missing || undefined} data-unpaid={e.unpaid_overdue || undefined}>
      <div className="min-w-0 flex-1 basis-[220px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold text-gray-900 truncate">{e.customer || '—'}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${st.cls}`} data-testid={`wl-status-${e.id}`}>{st.label}</span>
          {portal && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-700 text-white" data-testid={`wl-portal-${e.id}`}>From portal</span>}
        </div>
        {e.domain && (
          <a href={`https://${e.domain}`} target="_blank" rel="noreferrer" className="text-sm text-[#2F5E96] hover:underline inline-flex items-center gap-1 mt-0.5" data-testid={`wl-domain-${e.id}`}>{e.domain} <ExternalLink className="w-3 h-3" /></a>
        )}
        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {e.built_on && <span>Built {e.built_on}</span>}
          {e.built_by && <span>by {e.built_by}</span>}
          {e.hosting && <span>· {e.hosting}</span>}
        </div>
        {(e.contact_name || e.contact_phone || e.contact_email) && (
          <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-3">{e.contact_name && <span>{e.contact_name}</span>}{e.contact_phone && <a href={`tel:${e.contact_phone}`} className="hover:underline">{e.contact_phone}</a>}{e.contact_email && <a href={`mailto:${e.contact_email}`} className="hover:underline truncate max-w-[220px]">{e.contact_email}</a>}</div>
        )}
        {e.notes && <p className="text-xs text-gray-500 mt-1 whitespace-pre-line">{e.notes}</p>}
        <RenewalCell e={e} onSaved={onRenewal} />
        <HostingInvoiceCell e={e} onRaise={onRaise} onTakeDown={onTakeDown} />
      </div>
      <div className="flex items-center gap-1.5 ml-auto">
        {portal ? (
          <Button size="sm" variant="outline" onClick={() => navigate(`/staff/website-orders/${e.order_id}`)} data-testid={`wl-open-order-${e.id}`}>Open order</Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => onEdit(e)} aria-label="Edit" data-testid={`wl-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(e)} aria-label="Delete" className="text-rose-700 hover:bg-rose-50" data-testid={`wl-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function StaffWebsiteLog() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [params] = useSearchParams();
  const [data, setData] = useState({ manual: [], portal: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState(FILTERS.some(([k]) => k === params.get('filter')) ? params.get('filter') : 'all');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [raising, setRaising] = useState(null);

  const load = async () => {
    try { setData(await wget('/staff/website-log')); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const all = [...data.manual, ...data.portal].sort((a, b) => (b.built_on || '').localeCompare(a.built_on || ''));
    const s = q.trim().toLowerCase();
    return all.filter((e) => matches(e, filter))
      .filter((e) => !s || [e.customer, e.domain, e.contact_name, e.contact_email, e.contact_phone, e.hosting, e.built_by, e.notes].some((v) => (v || '').toLowerCase().includes(s)));
  }, [data, q, filter]);

  const takeDown = async (e) => {
    if (!(await confirm({ title: 'Mark this site as taken down?', body: `${e.customer || e.domain} will be marked offline with a dated note that hosting was unpaid. Remember to actually suspend it on the server.`, confirmLabel: 'Mark taken down', destructive: true }))) return;
    try { await wpost(`/staff/website-log/${e.id}/take-down`); toast.success('Marked as taken down'); load(); } catch (err) { toast.error(err.message); }
  };

  const setRenewal = (id, renewal_date) => setData((d) => ({
    ...d,
    manual: d.manual.map((e) => (e.id === id ? { ...e, renewal_date } : e)),
    portal: d.portal.map((e) => (e.id === id ? { ...e, renewal_date } : e)),
  }));

  const remove = async (e) => {
    if (!(await confirm({ title: 'Remove this website from the log?', body: `${e.customer || e.domain} will be deleted from the log (the website itself is untouched).`, confirmLabel: 'Remove', destructive: true }))) return;
    try { await wdel(`/staff/website-log/${e.id}`); toast.success('Removed'); load(); } catch (err) { toast.error(err.message); }
  };

  const everything = [...data.manual, ...data.portal];
  const counts = { total: everything.length, live: everything.filter((e) => e.status === 'live').length, missing: everything.filter((e) => renewalState(e) === 'missing').length,
    due: everything.filter((e) => matches(e, 'due')).length, unpaid: everything.filter((e) => e.unpaid_overdue).length };
  const chipCount = { missing_renewal: counts.missing, due: counts.due, unpaid: counts.unpaid };
  const chipTone = { missing_renewal: 'bg-amber-50 text-amber-900 border-amber-300 hover:border-amber-400', due: 'bg-amber-50 text-amber-900 border-amber-300 hover:border-amber-400', unpaid: 'bg-rose-50 text-rose-900 border-rose-300 hover:border-rose-400' };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="website-log-page">
      <header className="bg-[#27292E] text-white border-b-4 border-teal-600">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 flex flex-wrap items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg" aria-label="Back" data-testid="wl-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-[160px]">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2"><Globe className="w-5 h-5 text-teal-400" /> Website Log</h1>
            <p className="text-white/70 text-xs hidden sm:block">Every website we've built — past sites from the spreadsheet plus those built through the portal.</p>
          </div>
          <div className="w-full sm:w-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setPasteOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="wl-paste-open"><ClipboardPaste className="w-4 h-4 mr-1.5" /> Paste from spreadsheet</Button>
            <Button size="sm" onClick={() => setEditing({})} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="wl-add-open"><Plus className="w-4 h-4 mr-1" /> Add website</Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, domain, contact…" className="pl-8 h-9 bg-white" data-testid="wl-search" />
          </div>
          <span className="text-xs text-gray-500" data-testid="wl-counts">{counts.total} websites · {counts.live} live</span>
        </div>
        {counts.missing > 0 && (
          <button type="button" onClick={() => setFilter(filter === 'missing_renewal' ? 'all' : 'missing_renewal')} className="w-full text-left bg-amber-50 border border-amber-300 rounded-xl px-3 py-2.5 flex items-start gap-2 hover:bg-amber-100" data-testid="wl-missing-banner">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <span className="text-sm text-amber-900"><strong>{counts.missing} live website{counts.missing === 1 ? '' : 's'}</strong> {counts.missing === 1 ? 'has' : 'have'} no hosting renewal date yet — they're highlighted below. Add the date on each row so we know when hosting is due.</span>
          </button>
        )}
        {counts.unpaid > 0 && (
          <button type="button" onClick={() => setFilter(filter === 'unpaid' ? 'all' : 'unpaid')} className="w-full text-left bg-rose-50 border border-rose-300 rounded-xl px-3 py-2.5 flex items-start gap-2 hover:bg-rose-100" data-testid="wl-unpaid-banner">
            <AlertTriangle className="w-4 h-4 text-rose-700 mt-0.5 shrink-0" />
            <span className="text-sm text-rose-900"><strong>{counts.unpaid} website{counts.unpaid === 1 ? '' : 's'}</strong> {counts.unpaid === 1 ? 'has' : 'have'} a hosting invoice unpaid past the renewal date — {counts.unpaid === 1 ? 'it' : 'they'} should be taken down.</span>
          </button>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="wl-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`wl-filter-${k}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === k ? 'bg-[#27292E] text-white border-[#27292E]' : chipCount[k] ? chipTone[k] : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>{l}{chipCount[k] ? ` (${chipCount[k]})` : ''}</button>
          ))}
        </div>

        {loading ? <div className="text-center py-16 text-gray-500">Loading…</div>
          : rows.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center" data-testid="wl-empty">
              <Globe className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="font-semibold text-gray-800">{counts.total ? 'Nothing matches' : 'No websites logged yet'}</p>
              {!counts.total && <p className="text-sm text-gray-500 mt-1">Copy the rows from your Google Sheet and use <strong>Paste from spreadsheet</strong>, or add one at a time.</p>}
            </div>
          ) : (
            <div className="space-y-2" data-testid="wl-list">
              {rows.map((e) => <Row key={e.id} e={e} onEdit={setEditing} onDelete={remove} onRenewal={setRenewal} onRaise={setRaising} onTakeDown={takeDown} navigate={navigate} />)}
            </div>
          )}
      </div>

      <PasteImportDialog open={pasteOpen} onClose={() => setPasteOpen(false)} onImported={load} />
      <WebsiteEntryDialog open={!!editing} entry={editing} onClose={() => setEditing(null)} onSaved={load} />
      <HostingInvoiceDialog entry={raising} defaultPrice={data.hosting_default_price} xeroConnected={!!data.xero_connected} onClose={() => setRaising(null)} onRaised={load} />
    </div>
  );
}
